import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { PYTHON_URL } from '@/shared/config/base-url.token';
import { SKIP_RESOURCE_ERROR_REDIRECT } from '@/shared/config/http-context.config';

export interface FeedbackOpenOptions {
  readonly message?: string;
  readonly files?: File[];
  readonly estimateId?: string;
  readonly source?: string;
}

@Injectable({ providedIn: 'root' })
export class FeedbackService {
  readonly visible$: Signal<boolean>;
  readonly sending$: Signal<boolean>;

  readonly initialMessage$: Signal<string>;
  readonly initialFiles$: Signal<File[]>;
  readonly initialEstimateId$: Signal<string | null>;
  readonly initialSource$: Signal<string | null>;

  private readonly visibleSignal$: WritableSignal<boolean>;
  private readonly sendingSignal$: WritableSignal<boolean>;
  private readonly initialMessageSignal$: WritableSignal<string>;
  private readonly initialFilesSignal$: WritableSignal<File[]>;
  private readonly initialEstimateIdSignal$: WritableSignal<string | null>;
  private readonly initialSourceSignal$: WritableSignal<string | null>;

  constructor(
    @Inject(PYTHON_URL) private readonly baseUrl: string,
    private readonly http: HttpClient,
  ) {
    this.visibleSignal$ = signal(false);
    this.sendingSignal$ = signal(false);
    this.initialMessageSignal$ = signal('');
    this.initialFilesSignal$ = signal<File[]>([]);
    this.initialEstimateIdSignal$ = signal<string | null>(null);
    this.initialSourceSignal$ = signal<string | null>(null);

    this.visible$ = this.visibleSignal$.asReadonly();
    this.sending$ = this.sendingSignal$.asReadonly();
    this.initialMessage$ = this.initialMessageSignal$.asReadonly();
    this.initialFiles$ = this.initialFilesSignal$.asReadonly();
    this.initialEstimateId$ = this.initialEstimateIdSignal$.asReadonly();
    this.initialSource$ = this.initialSourceSignal$.asReadonly();
  }

  open(options?: FeedbackOpenOptions): void {
    this.initialMessageSignal$.set(options?.message ?? '');
    this.initialFilesSignal$.set(options?.files ?? []);
    this.initialEstimateIdSignal$.set(options?.estimateId ?? null);
    this.initialSourceSignal$.set(options?.source ?? null);
    this.visibleSignal$.set(true);
  }

  close(): void {
    this.visibleSignal$.set(false);
    this.initialMessageSignal$.set('');
    this.initialFilesSignal$.set([]);
    this.initialEstimateIdSignal$.set(null);
    this.initialSourceSignal$.set(null);
  }

  async send(
    message: string,
    photo: File | null = null,
    document: File | null = null,
    estimateId?: string | null,
    source?: string | null,
  ): Promise<boolean> {
    const context = new HttpContext().set(SKIP_RESOURCE_ERROR_REDIRECT, true);
    this.sendingSignal$.set(true);
    try {
      const formData = new FormData();
      formData.append('message', message.trim());
      if (photo) {
        formData.append('photo', photo);
      }
      if (document) {
        formData.append('document', document);
      }
      if (estimateId) {
        formData.append('estimate_id', estimateId);
      }
      if (source) {
        formData.append('source', source);
      }
      await lastValueFrom(this.http.post<{ status: string }>(`${this.baseUrl}/feedback/send`, formData, { context }));
    } catch (error) {
      console.error('Не удалось отправить обратную связь', error);
      return false;
    } finally {
      this.sendingSignal$.set(false);
    }
    this.visibleSignal$.set(false);
    return true;
  }
}
